import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { ClientLogger } from 'bernie-client';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { updateSearch } from 'bernie-core';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { PropertyOffers } from '@shared-ui/lodging-property-offers';

import { CollectionBusinessModel, INCOMPATIBLE_RATEPLAN } from 'stores/multi-item-store-util';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStore } from 'stores/multi-item-store';

import { UitkPageLoader } from 'uitk-react-loader';

import { adaptToCartPropertyPrimaryKeyInput, adaptToCartPropertySearchContextInput } from 'components/multi-item/cart-adapter';
import { parse } from 'query-string';
import { SharedUiWrapperProps, withSharedUiPropertyOffers } from './shared-ui-property-offers';

export interface SinglePill {
  type: string;
  name: string;
  selected: boolean;
}

export interface AggregatedPillOptions {
  id: string;
  primary: string;
  value: string;
  selected: boolean;
  disabled: boolean;
}

interface SharedUiPropertyOffersProps extends SharedUiWrapperProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  currentHotel?: CurrentHotelStore;
  experiment?: ExperimentStore;
  multiItem?: MultiItemStore;
  page?: PageStore;
}

const reserveFormLogger = ClientLogger.getLoggerWithIdentifier('pwa.component.reserveForm');

export const LinkWrapper: React.FC<{ href?: string }> = ({ href, children }) => {
  const location = useLocation();
  // Temporary until the correct url is returned from the bff.
  // The href value for the error message is only the queryparam, not the
  // whole path.
  // When fixed, no need for the pathname and search anymore
  // TODO: add bff PR
  if (href?.includes('?')) {
    return <Link to={href}>{children}</Link>;
  }

  return <Link to={{ pathname: location.pathname, search: href }}>{children}</Link>;
};

const paramsBuilder = (
  pillType: string,
  pillName: string,
  selected: boolean,
  currentParams: { [key: string]: string | string[] }
): string[] => {
  /**
   * Possible values for currentParams (eg)
   * paymentType = 'FREE_CANCELLATION,PAY_LATER'
   * amenities = ['PARKING','OCEAN_VIEW'];
   */
  const paramValue: string | string[] = currentParams[pillType];
  let preFilteredType = (typeof paramValue === 'string' && paramValue !== '' && paramValue.split(',')) || (paramValue as string[]) || [];

  if (selected && !preFilteredType.includes(pillName)) {
    preFilteredType.push(pillName);
  } else if (!selected && preFilteredType.includes(pillName)) {
    preFilteredType = preFilteredType.filter(item => item !== pillName);
  }

  return preFilteredType;
};

export const updateSearchAndFilters = (
  pill: SinglePill | AggregatedPillOptions[],
  history: ReturnType<typeof useHistory>,
  location: ReturnType<typeof useLocation>
): void => {
  let pillType = '';
  const currentParams: { [key: string]: string[] } = parse(location.search);

  if (pill instanceof Array) {
    pill.forEach(option => {
      const { id: type, value: name, selected } = option;
      pillType = type;
      currentParams[pillType] = paramsBuilder(pillType, name, selected, currentParams);
    });
  } else {
    const { type, name, selected } = pill;
    pillType = type;
    currentParams[pillType] = paramsBuilder(pillType, name, selected, currentParams);
  }

  updateSearch({ history, location, newParams: { [pillType]: currentParams[pillType].filter(Boolean).join() } });
};

/**
 * Represents two different shared-ui versions of room and rates depending on the offers data given.
 * e.g. if verticalListings, then renders TnL1A version of property-offers
 * otherwise renders control version
 */
const PropertyOffersWrapper: React.FC<SharedUiPropertyOffersProps> = ({
  children,
  inputs,
  analytics,
  context,
  currentHotel,
  multiItem,
}) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = parse(location.search);
  const handleMultiItemFailure = (formRef: { current: { submit: () => void } }) => (reason: string) => {
    if (multiItem?.itemCount === 0) {
      if (typeof formRef?.current?.submit === 'function') {
        formRef.current.submit();
      }
      reserveFormLogger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'FALLBACK_TO_STANDALONE_BOOKING'));
    }
    // this is treating rare usecase, where user tries to reserve a rateplan that they shouldn't have seen in first place
    else if (reason === INCOMPATIBLE_RATEPLAN) {
      reserveFormLogger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'INCOMPATIBLE_RATEPLAN'));
    }
  };

  const handleHotelPlusHotelCallBack = (
    propertyNaturalKeys: any,
    businessModel: CollectionBusinessModel,
    noCreditCard: boolean | undefined,
    multiItemPriceToken: any,
    formRef: { current: { submit: () => void } }
  ) =>
    context &&
    currentHotel &&
    multiItem
      ?.addPropertyToCart(
        context,
        adaptToCartPropertyPrimaryKeyInput(propertyNaturalKeys),
        adaptToCartPropertySearchContextInput(currentHotel.searchCriteria, queryParams.regionId),
        businessModel,
        analytics,
        noCreditCard
      )
      ?.catch(handleMultiItemFailure(formRef));

  const handleSelectProductsFailure = (reason: any) => {
    if (!multiItem?.errorMessage) {
      multiItem?.setGenericError();
    }
    reserveFormLogger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'FAILED_TO_ADD_HOTEL_TO_MULTIITEM_SESSION'), reason);
  };

  const handleMultiItemCallBack = (
    propertyNaturalKeys: any,
    businessModel: any,
    noCreditCard: any,
    multiItemPriceToken: string,
    formRef?: React.RefObject<HTMLFormElement>,
    dealMarker?: string
  ) => (
      context &&
      multiItem?.multiItemContext?.id &&
      multiItem
        ?.selectProducts(
          context,
          multiItem.multiItemContext.id,
          multiItemPriceToken,
          adaptToCartPropertyPrimaryKeyInput(propertyNaturalKeys),
          [],
          [],
          dealMarker
        )
        .then(redirectUrl => {
          window.location.href = redirectUrl;
        })
        .catch(handleSelectProductsFailure)
    );
  const isMultiItem = multiItem?.isPackageShoppingPath();

  // TODO This part is temporary to enable shared-ui-property-offers on all pages. This part will be moved to shared-ui as well
  const getMultiItemCallBack = () => {
    if (multiItem?.showMultiPropExperience) {
      return handleHotelPlusHotelCallBack;
    } else if (isMultiItem) {
      return handleMultiItemCallBack;
    }
  };

  const handleClick = (pill: SinglePill | AggregatedPillOptions[]): void => {
    updateSearchAndFilters(pill, history, location);
  };

  const offersProps = {
    inputs: inputs,
    defaultErrorMessage: multiItem?.genericError,
    LinkWrapper: LinkWrapper,
    updateSearchAndFilters: handleClick,
  };

  if (isMultiItem) {
    return (
      <Experiment name="MIT_PWA_PDP_Select_Products_Mutation_Callback_SharedUI">
        <ExperimentControl>
          <Experiment name="PDP_PCC_Value_Props_LCP_Performance_Improvement">
            <ExperimentControl>
              <PropertyOffers {...(offersProps as any)} handleMultiItemCallback={getMultiItemCallBack()} skipSsr={true}>
                {children}
              </PropertyOffers>
            </ExperimentControl>
            <ExperimentVariant bucket={1}>
              <PropertyOffers {...(offersProps as any)} handleMultiItemCallback={getMultiItemCallBack()} skipSsr={false}>
                {children}
              </PropertyOffers>
            </ExperimentVariant>
            <ExperimentVariant bucket={2}>
              <PropertyOffers {...(offersProps as any)} handleMultiItemCallback={getMultiItemCallBack()} skipSsr={true}>
                {children}
              </PropertyOffers>
            </ExperimentVariant>
          </Experiment>
          <UitkPageLoader visible={multiItem?.pageLoading} />
        </ExperimentControl>

        <ExperimentVariant bucket={1}>
          <Experiment name="PDP_PCC_Value_Props_LCP_Performance_Improvement">
            <ExperimentControl>
              <PropertyOffers {...(offersProps as any)} skipSsr={true}>
                {children}
              </PropertyOffers>
            </ExperimentControl>
            <ExperimentVariant bucket={1}>
              <PropertyOffers {...(offersProps as any)} skipSsr={false}>
                {children}
              </PropertyOffers>
            </ExperimentVariant>
            <ExperimentVariant bucket={2}>
              <PropertyOffers {...(offersProps as any)} skipSsr={true}>
                {children}
              </PropertyOffers>
            </ExperimentVariant>
          </Experiment>
        </ExperimentVariant>
      </Experiment>
    );
  }

  /**
   * Added ssr strategy under "PDP_PCC_Value_Props_LCP_Performance_Improvement" experiment to check for performance improvements
   */
  return (
    <Experiment name="PDP_PCC_Value_Props_LCP_Performance_Improvement">
      <ExperimentControl>
        <PropertyOffers {...(offersProps as any)} handleMultiItemCallback={getMultiItemCallBack()} skipSsr={true}>
          {children}
        </PropertyOffers>
      </ExperimentControl>
      <ExperimentVariant bucket={1}>
        <PropertyOffers {...(offersProps as any)} handleMultiItemCallback={getMultiItemCallBack()} skipSsr={false}>
          {children}
        </PropertyOffers>
      </ExperimentVariant>
      <ExperimentVariant bucket={2}>
        <PropertyOffers {...(offersProps as any)} handleMultiItemCallback={getMultiItemCallBack()} skipSsr={true}>
          {children}
        </PropertyOffers>
      </ExperimentVariant>
    </Experiment>
  );
};

const ObservedPropertyOffersWrapper: React.FC<SharedUiPropertyOffersProps> = inject(
  'analytics',
  'context',
  'currentHotel',
  'multiItem'
)(observer(props => <PropertyOffersWrapper {...props} />));
ObservedPropertyOffersWrapper.displayName = 'SharedUiPropertyOffers';
export const SharedUiOffersWrapper = withSharedUiPropertyOffers(ObservedPropertyOffersWrapper);
